import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getLitters, getSettings } from '../api';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import Section, { SectionTitle } from '../components/Section';
import { Cat, Lock, ArrowRight, Calendar, Heart, UserPlus, Snowflake } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const KittenPage = ({ user, onLogout }) => {
  const [settings, setSettings] = useState({});
  const [litters, setLitters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const promises = [getSettings()];
    if (user) {
      promises.push(getLitters({ active_only: true }));
    }
    Promise.all(promises)
      .then(([settingsRes, littersRes]) => {
        setSettings(settingsRes.data);
        if (littersRes) setLitters(littersRes.data.filter(l => l.is_published));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#EEF3F8]">
        <Snowflake size={48} className="text-[#7A9BB5] animate-spin" style={{ animationDuration: '3s' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EEF3F8]">
      <Navigation user={user} onLogout={onLogout} settings={settings} isLanding={false} />

      {/* Public Section - General kitten info */}
      <Section variant="ice">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#2E3A46] flex items-center justify-center mx-auto mb-6">
            <Cat size={28} className="text-[#7A9BB5]" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#2E3A46] mb-6">
            Unsere Kitten
          </h1>
          <p className="text-base md:text-lg text-[#354551] leading-relaxed max-w-2xl mx-auto mb-8">
            Bei den Schneeblumen wachsen alle Kitten als vollwertige Familienmitglieder auf.
            Die ersten Wochen verbringen sie eng bei ihrer Mutter und werden von uns liebevoll
            sozialisiert. Jedes Kitten wird individuell betreut und auf sein neues Zuhause vorbereitet.
          </p>
        </div>
      </Section>

      {/* Socialization Info */}
      <Section variant="even">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <div className="card-frost p-6 text-center">
            <Heart size={28} className="text-[#7A9BB5] mx-auto mb-3" />
            <h3 className="font-heading text-lg text-[#2E3A46] mb-2">Aufzucht mit Herz</h3>
            <p className="text-sm text-[#354551]">
              Unsere Kitten wachsen mitten in der Familie auf – mit viel Zuwendung, Spielzeit und Geborgenheit.
            </p>
          </div>
          <div className="card-frost p-6 text-center">
            <Calendar size={28} className="text-[#7A9BB5] mx-auto mb-3" />
            <h3 className="font-heading text-lg text-[#2E3A46] mb-2">Frühestens ab 14 Wochen</h3>
            <p className="text-sm text-[#354551]">
              Kein Kitten verlässt uns vor der 14. Lebenswoche – so stellen wir eine optimale Sozialisation sicher.
            </p>
          </div>
          <div className="card-frost p-6 text-center">
            <Cat size={28} className="text-[#7A9BB5] mx-auto mb-3" />
            <h3 className="font-heading text-lg text-[#2E3A46] mb-2">Gesundheitsvorsorge</h3>
            <p className="text-sm text-[#354551]">
              Individuelle Impfplanung, tierärztliche Untersuchungen und liebevolle Entwurmung gehören dazu.
            </p>
          </div>
        </div>
      </Section>

      {/* Protected Section - Litter details */}
      {user ? (
        litters.length > 0 ? (
          <Section variant="odd">
            <SectionTitle subtitle="Für registrierte Interessenten">
              Aktuelle Würfe
            </SectionTitle>
            <div className="space-y-8">
              {litters.map((litter) => (
                <div key={litter.id} className="card-frost p-6 md:p-8">
                  <h3 className="font-heading text-2xl text-[#2E3A46] mb-4">{litter.name}</h3>
                  
                  {/* Parents */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {litter.mother && (
                      <div className="flex items-center gap-4 p-4 rounded-xl bg-[#D8E8F0]">
                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-white shrink-0">
                          {litter.mother.photos?.length > 0 ? (
                            <img src={`${BACKEND_URL}${litter.mother.photos[0]}`} alt={litter.mother.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><Cat size={24} className="text-[#C5D0DA]" /></div>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-[#7A9BB5] uppercase tracking-wider">Mutter</p>
                          <p className="font-heading text-lg text-[#2E3A46]">{litter.mother.name}</p>
                          {litter.mother.base_color && <p className="text-xs text-[#7A9BB5]">{litter.mother.base_color}</p>}
                        </div>
                      </div>
                    )}
                    {litter.father && (
                      <div className="flex items-center gap-4 p-4 rounded-xl bg-[#D8E8F0]">
                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-white shrink-0">
                          {litter.father.photos?.length > 0 ? (
                            <img src={`${BACKEND_URL}${litter.father.photos[0]}`} alt={litter.father.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><Cat size={24} className="text-[#C5D0DA]" /></div>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-[#7A9BB5] uppercase tracking-wider">Vater</p>
                          <p className="font-heading text-lg text-[#2E3A46]">{litter.father.name}</p>
                          {litter.father.base_color && <p className="text-xs text-[#7A9BB5]">{litter.father.base_color}</p>}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Dates */}
                  <div className="flex flex-wrap gap-4 mb-6 text-sm">
                    {litter.born_at && (
                      <div className="flex items-center gap-1.5 text-[#7A9BB5]">
                        <Calendar size={14} /> Geboren: {litter.born_at}
                      </div>
                    )}
                    {litter.expected_birth_at && !litter.born_at && (
                      <div className="flex items-center gap-1.5 text-[#C9A96E]">
                        <Calendar size={14} /> Erwarteter Termin: {litter.expected_birth_at}
                      </div>
                    )}
                  </div>

                  {/* Kittens */}
                  {litter.kittens?.length > 0 && (
                    <div>
                      <h4 className="font-heading text-lg text-[#2E3A46] mb-3">Kitten</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {litter.kittens.map((kitten) => (
                          <div key={kitten.id} className="p-4 rounded-xl border border-[#C5D0DA] bg-white">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium text-[#2E3A46]">{kitten.name || 'Unbenannt'}</h5>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                kitten.status === 'available' ? 'bg-green-100 text-green-700' :
                                kitten.status === 'reserved' ? 'bg-[#C9A96E]/15 text-[#C9A96E]' :
                                'bg-[#C5D0DA]/30 text-[#7A9BB5]'
                              }`}>
                                {kitten.status === 'available' ? 'Verfügbar' :
                                 kitten.status === 'reserved' ? 'Reserviert' : 'Vergeben'}
                              </span>
                            </div>
                            {kitten.color && <p className="text-xs text-[#7A9BB5]">{kitten.color}</p>}
                            {kitten.gender && <p className="text-xs text-[#7A9BB5]">{kitten.gender === 'male' ? 'Männlich' : 'Weiblich'}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {litter.notes && (
                    <p className="text-sm text-[#354551] mt-4 italic">{litter.notes}</p>
                  )}
                </div>
              ))}
            </div>
          </Section>
        ) : (
          <Section variant="odd">
            <div className="text-center py-10">
              <Cat size={48} className="text-[#C5D0DA] mx-auto mb-4" />
              <h3 className="font-heading text-xl text-[#2E3A46] mb-2">Aktuell keine Würfe</h3>
              <p className="text-sm text-[#7A9BB5]">Wir informieren Sie, sobald es Neuigkeiten gibt.</p>
            </div>
          </Section>
        )
      ) : (
        /* Registration CTA for non-logged in users */
        <Section variant="dark-frame">
          <div className="max-w-2xl mx-auto text-center py-8">
            <Lock size={40} className="text-[#7A9BB5] mx-auto mb-4" />
            <h2 className="font-heading text-2xl sm:text-3xl text-[#EEF3F8] mb-4">
              Aktuelle Würfe nur für registrierte Interessenten
            </h2>
            <p className="text-[#C5D0DA] mb-8">
              Aktuelle Würfe, Elterntiere und alle Informationen zu unseren Kitten sind nur für
              registrierte Interessenten sichtbar. Registrieren Sie sich kostenlos und erhalten Sie
              Zugang zu unserem Wissensportal mit 16 Kapiteln rund um die Birmahaltung.
            </p>
            <Link
              to="/login"
              className="btn-primary inline-flex items-center gap-2 text-base px-8 py-3"
            >
              <UserPlus size={18} />
              Jetzt kostenlos registrieren
            </Link>
          </div>
        </Section>
      )}

      <Footer settings={settings} />
    </div>
  );
};

export default KittenPage;
